import React from "react";
import { useSelector } from "react-redux";
import { SalesLevelMultiSelectField } from "../Common/SalesLevelMultiSelectField";
import { ModelField, isSelectField } from "./RbacConfig";
import { TextField, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox } from "@mui/material";
import { selectSalesforceSalesLevelTree } from '../../support/slices/salesforceSalesLevelSlice';
import { getSalesLevelOptions } from '../../support/salesLevelOptions';

type Props = {
  field: ModelField;
  value: any;
  onChange: (value: any) => void;
  selectOptions?: { [key: string]: any[] };
  disabled?: boolean;
  data?: any;
};

export function AdminFormField({ field, value, onChange, selectOptions = {}, disabled, data = {} }: Props) {
  const salesLevelTree = useSelector(selectSalesforceSalesLevelTree);

  // Handle dynamic sales levels
  if (field.key.startsWith("sales_level_")) {
    const match = field.key.match(/^sales_level_(\d)$/);
    const level = match ? Number(match[1]) : 1;
    const path: string[] = [];
    for (let i = 1; i < level; i++) {
      const k = `sales_level_${i}`;
      const parentVal = (data[k] || "").split(",")[0]?.trim();
      if (parentVal) {
        path.push(parentVal);
      } else {
        break;
      }
    }
    const options = getSalesLevelOptions(salesLevelTree, level, path);

    return (
      <SalesLevelMultiSelectField
        label={field.label}
        value={value}
        onChange={onChange}
        options={options}
        disabled={disabled}
      />
    );
  }

  // Static enum-style select (fixed `options` list, no DB endpoint) — e.g. banner severity.
  if (isSelectField(field) && field.options) {
    const selectedValue = value ?? "";
    return (
      <FormControl fullWidth margin="normal" key={field.key}>
        <InputLabel>{field.label}</InputLabel>
        <Select
          value={selectedValue}
          label={field.label}
          onChange={e => onChange(e.target.value === "" ? null : e.target.value)}
          disabled={disabled}
        >
          {!field.required && (
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
          )}
          {field.options.map((opt) => (
            <MenuItem key={String(opt.value)} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  // Boolean → checkbox.
  if (field.type === "boolean") {
    return (
      <FormControlLabel
        key={field.key}
        control={
          <Checkbox
            checked={!!value}
            onChange={e => onChange(e.target.checked)}
            disabled={disabled}
          />
        }
        label={field.label}
      />
    );
  }

  // Datetime → native datetime-local input. Stores the "YYYY-MM-DDTHH:mm" string, which DRF's
  // DateTimeField parses; an empty value clears the (nullable) column.
  if (field.type === "datetime") {
    // ISO from the API ("2026-10-01T13:00:00Z") → the 16-char value the input expects.
    const inputValue = typeof value === "string" && value ? value.slice(0, 16) : "";
    return (
      <TextField
        key={field.key}
        type="datetime-local"
        label={field.label}
        value={inputValue}
        onChange={e => onChange(e.target.value === "" ? null : e.target.value)}
        fullWidth
        margin="normal"
        disabled={disabled}
        InputLabelProps={{ shrink: true }}
      />
    );
  }

  // Handle manager select with filtering
  if (isSelectField(field)) {
    let options = selectOptions[field.optionsEndpoint || ""] || selectOptions[field.key] || [];
    // Special manager filter
    if (field.key === "manager") {
      const currentEmail = data?.email;
      options = options.filter(
        (opt: any) => opt.is_manager && opt.email !== currentEmail
      );
    }
    const selectedValue = typeof value === "object" && value !== null ? value.id : value ?? "";

    return (
      <FormControl fullWidth margin="normal" key={field.key}>
        <InputLabel>{field.label}</InputLabel>
        <Select
          value={selectedValue}
          label={field.label}
          onChange={e => onChange(e.target.value === "" ? null : e.target.value)}
          disabled={disabled}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {options.map((opt: any) => {
            const label: string =
              (field.optionLabel && opt[field.optionLabel]) ||
              opt.name ||
              opt.title ||
              opt.email ||
              String(opt.id);
            return (
              <MenuItem key={opt.id} value={opt.id}>
                {label}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    );
  }

  if (field.type === "date") {
    return (
      <TextField
        label={field.label}
        type="date"
        value={value ?? ""}
        // '' is invalid for a DateField, send null
        onChange={e => onChange(e.target.value === "" ? null : e.target.value)}
        fullWidth
        margin="normal"
        InputLabelProps={{ shrink: true }}
        required={!!field.required}
        disabled={disabled}
      />
    );
  }

  if (field.type === "text") {
    const rows = (value?.length / 65) + 1 || 3
    return (
      <TextField
        label={field.label}
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        fullWidth
        margin="normal"
        // Add this:
        multiline={field.multiLine}
        minRows={rows}
      />
    );
  }

  // Handle text fields (default)
  return (
    <TextField
      fullWidth
      margin="normal"
      label={field.label}
      value={value ?? ""}
      onChange={e => onChange(e.target.value)}
      key={field.key}
      required={!!field.required}
      disabled={disabled}
    />
  );
}